import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import { projectsApi } from './projects'
import { logger } from '../../utils/logger'
import type { Tool, ToolCreate, CatalogCreatorOption } from './types'

// =============================================================================
// TOOL EXECUTION TYPES
// =============================================================================

export interface ToolExecuteRequest {
  action_id: string
  method?: string
  path?: string
  params?: Record<string, unknown>
  /** JSON object (template params) or raw string body (SOAP/XML/raw interactive tester). */
  body?: Record<string, unknown> | string
  headers?: Record<string, string>
  timeout?: number
}

export interface ToolExecuteResponse {
  success: boolean
  status_code: number | null
  headers: Record<string, string> | null
  body: unknown
  latency_ms: number
  request_id: string
  error: string | null
}

export interface ToolExecutionLog {
  id: string
  tool_id: string
  tool_display_name?: string | null
  action_id: string
  user_id: string | null
  request_method: string | null
  request_path: string | null
  request_params: Record<string, unknown> | null
  request_body: Record<string, unknown> | null
  request_headers: Record<string, string> | null
  response_status: number | null
  response_body: Record<string, unknown> | null
  response_headers: Record<string, string> | null
  latency_ms: number | null
  success: boolean
  error_message: string | null
  created_at: number
}

export interface ToolTestConnectionResponse {
  success: boolean
  status_code: number | null
  latency_ms: number
  error: string | null
}

export interface ToolAction {
  operation_id: string
  method: string
  path: string
  summary: string
  description: string
  /** OpenAPI parameter list, or JSON Schema object for HTTP/MCP actions. */
  parameters: unknown[] | Record<string, unknown>
  request_body: unknown
  responses: Record<string, unknown>
  /** HTTP tools also expose inputSchema (alias of parameters object). */
  inputSchema?: Record<string, unknown>
  name?: string
}

// =============================================================================
// MARKETPLACE TYPES
// =============================================================================

export interface ToolTemplate {
  id: string
  slug: string
  name: string
  description: string | null
  short_description: string | null
  icon_url: string | null
  banner_url: string | null
  category: string
  subcategory: string | null
  tags: string[] | null
  tool_type: string
  base_url: string | null
  spec_url: string | null
  auth_mode: string | null
  required_secrets: Array<{
    key: string
    label: string
    description: string
    required: boolean
    masked: boolean
    multiline?: boolean
    show_when?: Record<string, string>
  }> | null
  config_fields?: ToolTemplateConfigField[] | null
  provider_name: string | null
  provider_url: string | null
  documentation_url: string | null
  install_count: number
  average_rating: number | null
  rating_count: number
  status: string
  featured: boolean
  verified: boolean
  version: string
  featured_actions: Array<{
    id: string
    name: string
    importance: string
  }> | null
  created_at: number
  updated_at: number
}

export interface ToolTemplateConfigField {
  key: string
  label: string
  description?: string
  type: 'text' | 'select' | 'iam'
  required?: boolean
  placeholder?: string
  default?: string
  options?: Array<{ value: string; label: string }>
  show_when?: Record<string, string>
  /** When type=iam, optional filters for Cloud IAM list (OR across entries). */
  iam_filters?: Array<{ cloud_provider?: string; iam_subtype?: string }>
}

export interface ToolTemplateList {
  items: ToolTemplate[]
  total: number
  page: number
  page_size: number
  categories: Record<string, number>
}

export interface ToolCategory {
  id: string
  name: string
  description: string
  icon: string
  color: string
  tool_count: number
}

export interface ToolTemplateInstallRequest {
  secrets: Record<string, string>
  config_values?: Record<string, string>
  /** Omit or null for platform-wide tools */
  project_id?: string | null
  name_override?: string
  tags?: string[]
}

// =============================================================================
// TOOLS API
// =============================================================================

interface ToolRequestOptions {
  signal?: AbortSignal
  tenantId?: string
  /**
   * When false, a 401 from the backend proxy is surfaced as a normal error instead
   * of triggering the global session refresh / logout flow. Use for endpoints that
   * mirror downstream MCP or tool auth failures.
   */
  retryOn401?: boolean
}

export const toolsApi = {
  // =========================================================================
  // CRUD OPERATIONS
  // =========================================================================

  list: (params?: {
    search?: string
    page?: number
    page_size?: number
    project_id?: string
    tool_type?: string
    /** tools_registry.status: active | inactive | error | pending */
    registry_status?: string
    creator_id?: string
    tags?: string
    signal?: AbortSignal
    order_by?: string
    order_desc?: boolean
    omit_linked_for_project?: string
    only_approved_linked_for_project?: string
    include_project_membership_for?: string
    include_access_project_lists?: boolean
    include_catalog_filter_options?: boolean
    is_favourite?: boolean
    scope?: 'all'
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    logger.debug('toolsApi.list: Making request', {
      url: `/registry/tools${query}`,
      params: queryParams,
    })
    return request<{ items: Tool[]; total: number; catalog_creators?: CatalogCreatorOption[] }>(`/registry/tools${query}`, {
      signal,
    })
  },

  get: (id: string, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<Tool>(`/registry/tools/${id}`, {
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  create: (data: ToolCreate, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<Tool>('/registry/tools', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  update: (id: string, data: Partial<ToolCreate>, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<Tool>(`/registry/tools/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  delete: (id: string, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<void>(`/registry/tools/${id}`, {
      method: 'DELETE',
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  bulkDelete: (toolIds: string[], options?: { force?: boolean; signal?: AbortSignal; tenantId?: string }) =>
    request<{ deleted: number; skipped: number; errors: string[] }>('/registry/tools/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ tool_ids: toolIds }),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  // =========================================================================
  // TOOL EXECUTION
  // =========================================================================

  /**
   * Execute a tool action via backend proxy.
   * This handles authentication, logging, and error normalization.
   */
  execute: (toolId: string, data: ToolExecuteRequest, options?: ToolRequestOptions) =>
    request<ToolExecuteResponse>(`/registry/tools/${toolId}/execute`, {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      retryOn401: options?.retryOn401,
      tenantId: options?.tenantId,
    }),

  /**
   * Execute a tool action for a new tool before saving.
   */
  executeNew: (
    data: {
      tool_type: string
      endpoint?: string
      mcp_config?: Record<string, unknown>
      /** Full tool schema for HTTP dry-run (must include http_config). */
      schema?: Record<string, unknown>
      http_config?: Record<string, unknown>
      action_id: string
      params?: Record<string, unknown>
      auth_config?: Record<string, unknown>
      secrets?: Record<string, string>
      timeout?: number
    },
    options?: ToolRequestOptions
  ) =>
    request<ToolExecuteResponse>('/registry/tools/execute', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      retryOn401: options?.retryOn401,
      tenantId: options?.tenantId,
    }),

  /**
   * Get execution logs for a tool.
   */
  getExecutionLogs: (
    toolId: string,
    params?: { limit?: number; offset?: number; search?: string; signal?: AbortSignal }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: ToolExecutionLog[]; total: number }>(
      `/registry/tools/${toolId}/logs${query}`,
      {
        signal,
      }
    )
  },

  /**
   * Get execution logs for all tools.
   */
  listAllExecutionLogs: (params?: {
    tool_id?: string
    limit?: number
    offset?: number
    search?: string
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: ToolExecutionLog[]; total: number }>(`/registry/tools/logs${query}`, {
      signal,
    })
  },

  /**
   * Test if a tool endpoint is reachable.
   */
  testConnection: (toolId: string, options?: ToolRequestOptions) =>
    request<ToolTestConnectionResponse>(`/registry/tools/${toolId}/test-connection`, {
      method: 'POST',
      signal: options?.signal,
      retryOn401: options?.retryOn401,
      tenantId: options?.tenantId,
    }),

  /**
   * Test connection for a new tool before saving.
   */
  testNewConnection: (data: ToolCreate, options?: { signal?: AbortSignal }) =>
    request<ToolTestConnectionResponse>('/registry/tools/test-connection', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  /**
   * Get available actions for a tool.
   * Pass retryOn401: false — MCP/tool credential 401s must not clear the TAGP session.
   */
  getActions: (toolId: string, options?: ToolRequestOptions) =>
    request<{ actions: ToolAction[]; total: number }>(`/registry/tools/${toolId}/actions`, {
      signal: options?.signal,
      retryOn401: options?.retryOn401,
      tenantId: options?.tenantId,
    }),

  /**
   * Discover actions for a new tool before saving.
   */
  discoverActions: (data: ToolCreate, options?: ToolRequestOptions) =>
    request<{ actions: ToolAction[]; total: number }>('/registry/tools/discover', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      retryOn401: options?.retryOn401,
      tenantId: options?.tenantId,
    }),

  // =========================================================================
  // MARKETPLACE
  // =========================================================================

  /**
   * List templates from the marketplace.
   */
  listTemplates: (params?: {
    category?: string
    search?: string
    featured?: boolean
    page?: number
    page_size?: number
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<ToolTemplateList>(`/registry/tools/marketplace${query}`, {
      signal,
      // Hand-rolled callers (MarketplacePage, AddToolModal) bypass React Query —
      // enable API-layer retry so 429s honor Retry-After via retryWithBackoff().
      retry: true,
    })
  },

  /**
   * Get a specific template by slug.
   */
  getTemplate: (slug: string, options?: { signal?: AbortSignal }) =>
    request<ToolTemplate>(`/registry/tools/marketplace/${slug}`, {
      signal: options?.signal,
    }),

  /**
   * Install a template as a new tool.
   */
  installTemplate: (
    slug: string,
    data: ToolTemplateInstallRequest,
    options?: { signal?: AbortSignal; tenantId?: string }
  ) =>
    request<Tool>(`/registry/tools/marketplace/${slug}/install`, {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  /**
   * Rate a template.
   */
  rateTemplate: (slug: string, rating: number, options?: { signal?: AbortSignal }) =>
    request<ToolTemplate>(`/registry/tools/marketplace/${slug}/rate`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
      signal: options?.signal,
    }),

  /**
   * Get all tool categories.
   */
  getCategories: (options?: { signal?: AbortSignal }) =>
    request<{ categories: ToolCategory[] }>('/registry/tools/categories', {
      signal: options?.signal,
    }),

  // =========================================================================
  // ACCESS CONTROL
  // =========================================================================

  getAccessStatistics: (id: string) => projectsApi.getToolAccessStatistics(id),

  listAccessRequests: (
    id: string,
    params?: { status?: string; page?: number; page_size?: number }
  ) => projectsApi.listToolAccessRequests(id, params),

  approveAccess: (id: string, requestId: string, action: 'approve' | 'reject') =>
    projectsApi.approveToolAccess(id, requestId, action),

  revokeAccess: (id: string, requestId: string) => projectsApi.revokeToolAccess(id, requestId),
}
